import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { csrfFetch } from '@/lib/csrfFetch';
import { useCan } from '@/lib/utils';
import { type SharedData } from '@/types';
import { usePage } from '@inertiajs/react';
import { AlertTriangle } from 'lucide-react';
import { type ReactNode, FormEvent, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

type BugSeverity = 'low' | 'medium' | 'high' | 'critical';

interface BrowserDetails {
    browserName: string;
    browserVersion: string | null;
    osName: string;
    deviceType: 'desktop' | 'mobile' | 'tablet';
}

function parseBrowserDetails(userAgent: string): BrowserDetails {
    const ua = userAgent.toLowerCase();
    const browserMap: Array<{ name: string; pattern: RegExp }> = [
        { name: 'Edge', pattern: /edg\/([\d.]+)/ },
        { name: 'Chrome', pattern: /chrome\/([\d.]+)/ },
        { name: 'Firefox', pattern: /firefox\/([\d.]+)/ },
        { name: 'Safari', pattern: /version\/([\d.]+).*safari/ },
    ];

    let browserName = 'Unknown';
    let browserVersion: string | null = null;
    for (const browser of browserMap) {
        const match = ua.match(browser.pattern);
        if (match) {
            browserName = browser.name;
            browserVersion = match[1] ?? null;
            break;
        }
    }

    let osName = 'Unknown';
    if (ua.includes('windows')) {
        osName = 'Windows';
    } else if (ua.includes('mac os')) {
        osName = 'macOS';
    } else if (ua.includes('android')) {
        osName = 'Android';
    } else if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ios')) {
        osName = 'iOS';
    } else if (ua.includes('linux')) {
        osName = 'Linux';
    }

    let deviceType: BrowserDetails['deviceType'] = 'desktop';
    if (ua.includes('tablet') || ua.includes('ipad')) {
        deviceType = 'tablet';
    } else if (ua.includes('mobile') || ua.includes('iphone') || ua.includes('android')) {
        deviceType = 'mobile';
    }

    return { browserName, browserVersion, osName, deviceType };
}

function inferModule(pathname: string): string | null {
    const segment = pathname.split('/').filter(Boolean)[0];
    return segment ?? null;
}

function inferEntity(pathname: string): { entityType: string | null; entityId: string | null } {
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length < 2) {
        return { entityType: null, entityId: null };
    }

    const possibleId = segments[1] ?? null;
    const isEntityId = possibleId !== null && possibleId !== 'create' && possibleId !== 'edit';
    return {
        entityType: segments[0] ?? null,
        entityId: isEntityId ? possibleId : null,
    };
}

export function BugReportModal({ trigger }: { trigger?: ReactNode } = {}) {
    const { t } = useTranslations();
    const { can } = useCan();
    const page = usePage<SharedData>();
    const [open, setOpen] = useState(false);
    const [description, setDescription] = useState('');
    const [severity, setSeverity] = useState<BugSeverity>('medium');
    const [blockingWork, setBlockingWork] = useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const [processing, setProcessing] = useState(false);
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    const browserPreview = typeof navigator !== 'undefined' ? parseBrowserDetails(navigator.userAgent).browserName : 'Unknown';

    const canCreate = can('bug-reports.create');
    const unknownLabel = t('navigation.bug_report_modal.unknown', 'Unknown');
    const rolePreview = useMemo(() => {
        const roles = (page.props.auth as { roles?: string[] } | undefined)?.roles ?? [];
        return roles.length > 0 ? roles.join(', ') : unknownLabel;
    }, [page.props.auth, unknownLabel]);

    if (!canCreate) {
        return null;
    }

    const resetForm = () => {
        setDescription('');
        setSeverity('medium');
        setBlockingWork(false);
        setFiles([]);
    };

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (description.trim().length < 10) {
            toast.error(t('navigation.bug_report_modal.error_short_description', 'Description should be at least 10 characters.'));
            return;
        }

        const currentUrl = window.location.href;
        const pathname = window.location.pathname;
        const module = inferModule(pathname);
        const entity = inferEntity(pathname);
        const browser = parseBrowserDetails(navigator.userAgent);
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const locale = navigator.language;
        let routeName: string | null = null;
        try {
            const currentRoute = route().current();
            routeName = typeof currentRoute === 'string' ? currentRoute : null;
        } catch {
            routeName = null;
        }

        const formData = new FormData();
        formData.append('description', description.trim());
        formData.append('severity_suggested', severity);
        formData.append('blocking_work', blockingWork ? '1' : '0');
        if (module) {
            formData.append('module', module);
            formData.append('context[module]', module);
        }

        formData.append('context[url]', currentUrl);
        formData.append('context[path]', pathname);
        if (typeof routeName === 'string') {
            formData.append('context[route_name]', routeName);
        }
        formData.append('context[page_title]', document.title ?? '');
        if (entity.entityType) {
            formData.append('context[entity_type]', entity.entityType);
        }
        if (entity.entityId) {
            formData.append('context[entity_id]', entity.entityId);
        }
        if (document.referrer) {
            formData.append('context[referrer]', document.referrer);
        }

        formData.append('technical[browser_name]', browser.browserName);
        if (browser.browserVersion) {
            formData.append('technical[browser_version]', browser.browserVersion);
        }
        formData.append('technical[os_name]', browser.osName);
        formData.append('technical[device_type]', browser.deviceType);
        formData.append('technical[screen_width]', String(window.screen.width));
        formData.append('technical[screen_height]', String(window.screen.height));
        formData.append('technical[viewport_width]', String(window.innerWidth));
        formData.append('technical[viewport_height]', String(window.innerHeight));
        formData.append('technical[timezone]', timezone);
        formData.append('technical[locale]', locale);
        formData.append('technical[user_agent]', navigator.userAgent);

        for (const file of files) {
            formData.append('attachments[]', file);
        }

        setProcessing(true);
        try {
            const response = await csrfFetch(route('bug-reports.store'), {
                method: 'POST',
                body: formData,
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                const errorMessage =
                    payload?.message || t('navigation.bug_report_modal.error_submit_failed', 'Bug report could not be submitted. Please try again.');
                throw new Error(errorMessage);
            }

            toast.success(payload?.message || t('navigation.bug_report_modal.success', 'Bug report submitted. Thank you.'));
            resetForm();
            setOpen(false);
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : t('navigation.bug_report_modal.error_submit_failed', 'Bug report could not be submitted. Please try again.'),
            );
        } finally {
            setProcessing(false);
        }
    };

    return (
        <ResponsiveModal
            open={open}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (!nextOpen) {
                    resetForm();
                }
            }}
            variant="sheet"
        >
            <ResponsiveModalTrigger asChild>
                {trigger ?? (
                    <Button variant="ghost" size="icon" className="group h-9 w-9 cursor-pointer" title={t('navigation.report_bug', 'Report bug')}>
                        <AlertTriangle className="!size-5 opacity-80 group-hover:opacity-100" />
                    </Button>
                )}
            </ResponsiveModalTrigger>
            <ResponsiveModalContent size="lg">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('navigation.bug_report_modal.title', 'Report bug')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t(
                            'navigation.bug_report_modal.description',
                            'Describe what happened. Technical context from this page is added automatically.',
                        )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
                    <ResponsiveModalBody className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="bug-description">{t('navigation.bug_report_modal.label_description', 'What happened?')}</Label>
                            <Textarea
                                id="bug-description"
                                value={description}
                                onChange={(event) => setDescription(event.target.value)}
                                rows={5}
                                maxLength={5000}
                                required
                            />
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="bug-severity">{t('navigation.bug_report_modal.label_severity', 'How serious is it?')}</Label>
                                <Select value={severity} onValueChange={(value: BugSeverity) => setSeverity(value)}>
                                    <SelectTrigger id="bug-severity" className="w-full">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low">{t('navigation.bug_report_modal.severity_low', 'Low')}</SelectItem>
                                        <SelectItem value="medium">{t('navigation.bug_report_modal.severity_medium', 'Medium')}</SelectItem>
                                        <SelectItem value="high">{t('navigation.bug_report_modal.severity_high', 'High')}</SelectItem>
                                        <SelectItem value="critical">{t('navigation.bug_report_modal.severity_critical', 'Critical')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="bug-attachments">
                                    {t('navigation.bug_report_modal.label_attachments', 'Attach screenshot or file')}
                                </Label>
                                <Input
                                    id="bug-attachments"
                                    type="file"
                                    multiple
                                    accept=".png,.jpg,.jpeg,.webp,.pdf,.txt,.log"
                                    onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
                                />
                            </div>
                        </div>

                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                className="border-input h-4 w-4 rounded"
                                checked={blockingWork}
                                onChange={(event) => setBlockingWork(event.target.checked)}
                            />
                            <span>{t('navigation.bug_report_modal.blocking_work', 'This blocks my work')}</span>
                        </label>

                        <details className="rounded-md border p-3 text-sm">
                            <summary className="cursor-pointer font-medium">
                                {t('navigation.bug_report_modal.tech_details_summary', 'Technical details automatically included')}
                            </summary>
                            <div className="text-muted-foreground mt-2 space-y-1">
                                <p>
                                    {t('navigation.bug_report_modal.tech_page', 'Page')}: {currentPath || unknownLabel}
                                </p>
                                <p>
                                    {t('navigation.bug_report_modal.tech_module', 'Module')}: {inferModule(currentPath) ?? unknownLabel}
                                </p>
                                <p>
                                    {t('navigation.bug_report_modal.tech_browser', 'Browser')}:{' '}
                                    {browserPreview === 'Unknown' ? unknownLabel : browserPreview}
                                </p>
                                <p>
                                    {t('navigation.bug_report_modal.tech_user_role', 'User role')}: {rolePreview}
                                </p>
                                <p>
                                    {t('navigation.bug_report_modal.tech_time', 'Time')}: {new Date().toLocaleString()}
                                </p>
                            </div>
                        </details>
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={processing}>
                            {t('navigation.bug_report_modal.cancel', 'Cancel')}
                        </Button>
                        <Button type="submit" disabled={processing}>
                            {processing
                                ? t('navigation.bug_report_modal.submitting', 'Submitting...')
                                : t('navigation.bug_report_modal.submit', 'Submit report')}
                        </Button>
                    </ResponsiveModalFooter>
                </form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
